// ─── AFFILIATE DASHBOARD ──────────────────────────────────────────────────────
// Standalone dashboard for referral affiliates to track their subscribers & commissions
// Access via: yoursite.com/affiliate? or yoursite.com?view=affiliate?
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import {
  buildReferralLink,
  DEFAULT_COMMISSION_STRUCTURE, MONTHLY_REVENUE, calculateAffiliateEarnings,
} from '../utils/referral';
import { getOperatingCostConfig } from '../utils/planUtils';

// ─── STATUS BADGE CONFIG ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  trial:     { label: 'Trial',     color: '#ffaa00', bg: 'rgba(255,170,0,.12)',  icon: '⏳' },
  active:    { label: 'Active',    color: '#00ff88', bg: 'rgba(0,255,136,.12)',   icon: '✅' },
  churned:   { label: 'Churned',   color: '#ff4d4d', bg: 'rgba(255,77,77,.12)',   icon: '❌' },
  cancelled: { label: 'Cancelled', color: '#888',    bg: 'rgba(136,136,136,.12)', icon: '🚫' },
};

export default function AffiliateDashboard() {
  const [user, setUser] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [opConfig, setOpConfig] = useState({ monthlyOperatingCost: 40, leeway: 1 });

  // ─── AUTH LISTENER ──────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        await loadAffiliateData(u);
      } else {
        setUser(null);
        setAffiliate(null);
        setReferrals([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ─── LOAD AFFILIATE DATA ─────────────────────────────────────────────────
  const loadAffiliateData = async (u) => {
    try {
      console.log('[affiliate] user.uid:', u.uid);
      console.log('[affiliate] fetching path:', `affiliates/${u.uid}`);
      const affiliateDocRef = doc(db, 'affiliates', u.uid);
      const affiliateSnap = await getDoc(affiliateDocRef);
      console.log('[affiliate] snapshot.exists:', affiliateSnap.exists());
      console.log('[affiliate] snapshot.data:', affiliateSnap.data());
      console.log('[affiliate] isActive:', affiliateSnap.data()?.isActive);
      if (!affiliateSnap.exists() || !affiliateSnap.data().isActive) {
        setAffiliate(null);
        return;
      }
      setAffiliate({ id: affiliateSnap.id, ...affiliateSnap.data() });

      // Load operating cost config
      try {
        const cfg = await getOperatingCostConfig();
        setOpConfig(cfg);
      } catch {}

      // Fetch all referrals for this affiliate
      const q2 = query(
        collection(db, 'referrals'),
        where('affiliateId', '==', affiliateSnap.id)
      );
      const snapshot = await getDocs(q2);
      const refs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Cleanup: remove referrals for users that no longer exist
      const valid = [];
      for (const ref of refs) {
        try {
          const userDoc = await getDoc(doc(db, 'users', ref.userId));
          if (userDoc.exists()) {
            valid.push(ref);
          } else {
            deleteDoc(doc(db, 'referrals', ref.id)).catch(() => {});
          }
        } catch (e) {
          valid.push(ref); // keep on error (don't delete what we can't verify)
        }
      }
      setReferrals(valid);
    } catch (err) {
      console.error('[AffiliateDashboard] Error loading affiliate data:', err);
    }
  };

  // ─── LOGIN HANDLER ──────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setLoginError('Invalid email or password');
      } else if (err.code === 'auth/user-not-found') {
        setLoginError('No account found with this email');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
    setLoginLoading(false);
  };

  // ─── COPY LINK ──────────────────────────────────────────────────────────
  const copyLink = () => {
    const link = buildReferralLink(affiliate.referralCode);
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ─── COMPUTED STATS ─────────────────────────────────────────────────────
  const stats = React.useMemo(() => {
    if (!affiliate) return null;

    const active  = referrals.filter(r => r.status === 'active');
    const trial   = referrals.filter(r => r.status === 'trial');
    const churned = referrals.filter(r => r.status === 'churned' || r.status === 'cancelled');

    const earnings = calculateAffiliateEarnings(
      affiliate,
      opConfig.monthlyOperatingCost,
      opConfig.leeway
    );

    const totalEarned   = affiliate.totalEarnings  || 0;
    const totalPaid     = affiliate.totalPaid       || 0;
    const pendingPayout = affiliate.pendingPayout   || earnings.totalMonthlyCommission;

    const conversionRate = referrals.length > 0
      ? ((active.length / referrals.length) * 100).toFixed(1)
      : '0.0';

    const structure = affiliate.commissionStructure || DEFAULT_COMMISSION_STRUCTURE;

    return {
      totalReferrals: referrals.length,
      activeCount:    active.length,
      trialCount:     trial.length,
      churnedCount:   churned.length,
      conversionRate,
      totalEarned,
      totalPaid,
      pendingPayout,
      earnings,   // the full calculateAffiliateEarnings result
      structure,
      monthlyCommission: earnings.totalMonthlyCommission,
    };
  }, [referrals, affiliate, opConfig]);

  // ─── FILTERED & SORTED REFERRALS ───────────────────────────────────────
  const filteredReferrals = React.useMemo(() => {
    let filtered = [...referrals];
    if (filter !== 'all') filtered = filtered.filter(r => r.status === filter);
    filtered.sort((a, b) => {
      const dateA = (a.signupDate && a.signupDate.seconds) || 0;
      const dateB = (b.signupDate && b.signupDate.seconds) || 0;
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return filtered;
  }, [referrals, filter, sortBy]);

  // ─── FORMAT DATE ────────────────────────────────────────────────────────
  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ─── LOADING STATE ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // ─── LOGIN SCREEN ──────────────────────────────────────────────────────
  if (!user) {
    return (
      <div style={styles.loginWrap}>
        <div style={styles.loginCard}>
          <div style={styles.loginLogo}>
            <span style={styles.loginLogoText}>TRACKSY</span>
            <span style={styles.loginLogoSub}>Affiliate Portal</span>
          </div>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                style={styles.input}
                placeholder="affiliate?@email.com"
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Password</label>
              <div style={styles.passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  style={{ ...styles.input, paddingRight: 48 }}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeBtn}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            {loginError && <div style={styles.errorMsg}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn} disabled={loginLoading}>
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── NOT AN AFFILIATE ──────────────────────────────────────────────────────
  if (!affiliate) {
    return (
      <div style={styles.loginWrap}>
        <div style={styles.loginCard}>
          <div style={styles.loginLogo}>
            <span style={styles.loginLogoText}>TRACKSY</span>
            <span style={styles.loginLogoSub}>Affiliate Portal</span>
          </div>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <span style={{ fontSize: 48 }}>🔒</span>
            <p style={{ color: 'rgba(255,255,255,.7)', marginTop: 16, fontSize: 15, lineHeight: 1.6 }}>
              This account doesn't have affiliate access.<br />
              Contact the Tracksy team to get set up.
            </p>
            <p style={{ color: 'rgba(255,255,255,.4)', fontSize: 13, marginTop: 12 }}>
              Signed in as: {user.email}
            </p>
          </div>
          <button onClick={() => signOut(auth)} style={styles.logoutBtn}>Sign Out</button>
        </div>
      </div>
    );
  }

  // ─── MAIN AFFILIATE DASHBOARD ─────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>TRACKSY</h1>
          <p style={styles.headerSub}>Affiliate Dashboard</p>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.affiliateName}>{affiliate.name}</span>
          <button onClick={() => signOut(auth)} style={styles.headerLogout}>Sign Out</button>
        </div>
      </div>

      {/* REFERRAL LINK BANNER */}
      <div style={styles.linkBanner}>
        <div style={styles.linkBannerInner}>
          <div>
            <p style={styles.linkLabel}>Your Referral Link</p>
            <p style={styles.linkUrl}>{buildReferralLink(affiliate.referralCode)}</p>
          </div>
          <button onClick={copyLink} style={styles.copyBtn}>
            {copied ? '✅ Copied!' : '📋 Copy'}
          </button>
        </div>
        <div style={styles.linkCode}>
          Code: <span style={styles.codeHighlight}>{affiliate.referralCode}</span>
        </div>
      </div>

      {/* STATS GRID */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard, borderColor: 'rgba(0,255,136,.25)' }}>
            <p style={styles.statLabel}>Monthly Commission</p>
            <p style={{ ...styles.statValue, color: '#00ff88' }}>
              ${stats.monthlyCommission.toFixed(2)}
            </p>
            <p style={styles.statSub}>recurring</p>
          </div>
          <div style={{ ...styles.statCard, borderColor: 'rgba(0,204,255,.25)' }}>
            <p style={styles.statLabel}>Active Subscribers</p>
            <p style={{ ...styles.statValue, color: '#00ccff' }}>{stats.activeCount}</p>
            <p style={styles.statSub}>paying users</p>
          </div>
          <div style={{ ...styles.statCard, borderColor: 'rgba(255,170,0,.25)' }}>
            <p style={styles.statLabel}>In Trial</p>
            <p style={{ ...styles.statValue, color: '#ffaa00' }}>{stats.trialCount}</p>
            <p style={styles.statSub}>pending conversion</p>
          </div>
          <div style={{ ...styles.statCard, borderColor: 'rgba(255,255,255,.12)' }}>
            <p style={styles.statLabel}>Total Referrals</p>
            <p style={styles.statValue}>{stats.totalReferrals}</p>
            <p style={styles.statSub}>{stats.conversionRate}% conversion</p>
          </div>
        </div>
      )}

      {/* PROGRESS / THRESHOLD STATUS */}
      <div style={styles.earningsCard}>
        <h3 style={styles.sectionTitle}>📊 Progress</h3>
        {stats && (() => {
          const { earnings } = stats;
          const pct = earnings.effectiveThreshold > 0
            ? Math.min(100, (earnings.totalMonthlyRevenue / earnings.effectiveThreshold) * 100)
            : 100;

          return earnings.thresholdMet ? (
            <div>
              <div style={{
                padding: '14px', borderRadius: 12, background: 'rgba(0,255,136,.06)',
                border: '1px solid rgba(0,255,136,.2)', marginBottom: 12,
              }}>
                <p style={{ color: '#00ff88', fontWeight: 800, fontSize: 14, margin: 0 }}>
                  ✅ Threshold met!
                </p>
                <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 13, marginTop: 4 }}>
                  Earning commission on {earnings.subscribersEarningCommission} subscriber{earnings.subscribersEarningCommission !== 1 ? 's' : ''} · ${earnings.totalMonthlyCommission.toFixed(2)}/mo
                </p>
              </div>
              <div style={{ height: 8, background: 'rgba(255,255,255,.08)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: '100%', background: '#00ff88', borderRadius: 4 }} />
              </div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', margin: 0 }}>
                {earnings.subscribersCoveringThreshold} subscriber{earnings.subscribersCoveringThreshold !== 1 ? 's' : ''} covering operating costs
              </p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginBottom: 10 }}>
                Your referrals generate <strong style={{ color: '#fff' }}>{pct.toFixed(0)}%</strong> of the way to unlocking commissions
              </p>
              <div style={{ height: 8, background: 'rgba(255,255,255,.08)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#ffaa00', borderRadius: 4, transition: 'width .3s' }} />
              </div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', margin: 0 }}>
                Add more referrals to start earning commission
              </p>
            </div>
          );
        })()}
      </div>

      {/* EARNINGS BREAKDOWN */}
      {stats && (
        <div style={styles.earningsCard}>
          <h3 style={styles.sectionTitle}>💰 Earnings</h3>
          <div style={styles.earningsGrid}>
            <div style={styles.earningsRow}>
              <span style={styles.earningsLabel}>Monthly Commission</span>
              <span style={{ ...styles.earningsValue, color: '#00ff88' }}>
                ${stats.monthlyCommission.toFixed(2)}/mo
              </span>
            </div>
            <div style={styles.earningsRow}>
              <span style={styles.earningsLabel}>Pending Payout</span>
              <span style={{ ...styles.earningsValue, color: '#00ff88' }}>
                ${stats.pendingPayout.toFixed(2)}
              </span>
            </div>
            <div style={styles.earningsRow}>
              <span style={styles.earningsLabel}>Total Earned (All Time)</span>
              <span style={styles.earningsValue}>${stats.totalEarned.toFixed(2)}</span>
            </div>
            <div style={styles.earningsRow}>
              <span style={styles.earningsLabel}>Total Paid Out</span>
              <span style={styles.earningsValue}>${stats.totalPaid.toFixed(2)}</span>
            </div>
          </div>
          <div style={styles.commissionNote}>
            <span style={styles.commissionIcon}>ℹ️</span>
            <span>
              You earn <strong style={{ color: '#00ff88' }}>
              {Math.round((stats.structure.coreMonthly ?? 0.50) * 100)}%
            </strong> of every referred subscriber's fee — monthly or yearly — for life.
            </span>
          </div>
        </div>
      )}

      {/* REFERRALS TABLE */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h3 style={styles.sectionTitle}>👥 Referrals</h3>
          <div style={styles.tableFilters}>
            {['all', 'active', 'trial', 'churned'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  ...styles.filterBtn,
                  ...(filter === f ? styles.filterBtnActive : {})
                }}
              >
                {f === 'all' ? 'All' : (STATUS_CONFIG[f] && STATUS_CONFIG[f].label) || f}
              </button>
            ))}
          </div>
        </div>

        {filteredReferrals.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={{ fontSize: 40 }}>📭</span>
            <p style={{ color: 'rgba(255,255,255,.5)', marginTop: 12, fontSize: 14 }}>
              {referrals.length === 0
                ? 'No referrals yet. Share your link to get started!'
                : 'No referrals match this filter.'}
            </p>
          </div>
        ) : (
          <div style={styles.referralList}>
            {filteredReferrals.map(ref => {
              const cfg = STATUS_CONFIG[ref.status] || STATUS_CONFIG.trial;

              // Determine plan + billingCycle from stored fields (with legacy fallback)
              // Note: parentheses around ternary required — || binds tighter than ?:
              const plan = ref.plan || ((ref.subscriptionPlan || '').toLowerCase().includes('pro') ? 'pro' : 'core');
              const billingCycle = ref.billingCycle || 'monthly';

              // Per-subscriber earning: use stored commissionPerCycle if set, else fallback
              let monthlyEarning = 0;
              if (ref.status === 'active') {
                // Primary: use stored commissionPerCycle if set
                if (typeof ref.commissionPerCycle === 'number' && ref.commissionPerCycle > 0) {
                  monthlyEarning = ref.commissionPerCycle;
                } else if (stats && stats.earnings.thresholdMet) {
                  // Fallback: if threshold met, calculate from MONTHLY_REVENUE
                  const normalizedPlan = (ref.plan || '').toLowerCase();
                  const normalizedCycle = (ref.billingCycle || 'monthly').toLowerCase();
                  const planKey = `${normalizedPlan}${normalizedCycle === 'yearly' ? 'Yearly' : 'Monthly'}`;
                  const structure = affiliate?.commissionStructure || DEFAULT_COMMISSION_STRUCTURE;
                  const rate = structure[planKey] ?? 0.50;
                  monthlyEarning = parseFloat(((MONTHLY_REVENUE[planKey] || 0) * rate).toFixed(2));
                }
              }
              const earningLabel = ref.billingCycle === 'yearly' ? '/yr' : '/mo';

              return (
                <div key={ref.id} style={styles.referralCard}>
                  <div style={styles.referralTop}>
                    <div>
                      <p style={styles.referralEmail}>{ref.userEmail}</p>
                      <p style={styles.referralDate}>Signed up {formatDate(ref.signupDate)}</p>
                    </div>
                    <div style={{ ...styles.statusBadge, background: cfg.bg, borderColor: cfg.color + '40' }}>
                      <span>{cfg.icon}</span>
                      <span style={{ color: cfg.color, fontWeight: 700, fontSize: 12 }}>{cfg.label}</span>
                    </div>
                  </div>
                  <div style={styles.referralBottom}>
                    <div style={styles.referralStat}>
                      <span style={styles.referralStatLabel}>Plan</span>
                      <span style={styles.referralStatValue}>{ref.subscriptionPlan || 'Trial'}</span>
                    </div>
                    <div style={styles.referralStat}>
                      <span style={styles.referralStatLabel}>Billing</span>
                      <span style={styles.referralStatValue}>{ref.billingCycle || 'Monthly'}</span>
                    </div>
                    <div style={styles.referralStat}>
                      <span style={styles.referralStatLabel}>Monthly Fee</span>
                      <span style={styles.referralStatValue}>${(ref.monthlyRate || 0).toFixed(2)}</span>
                    </div>
                    <div style={styles.referralStat}>
                      <span style={styles.referralStatLabel}>Your Cut</span>
                      <span style={{ ...styles.referralStatValue, color: monthlyEarning > 0 ? '#00ff88' : 'rgba(255,255,255,.4)' }}>
                        ${monthlyEarning.toFixed(2)}{earningLabel}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* QUICK SHARE SECTION */}
      <div style={styles.shareCard}>
        <h3 style={styles.sectionTitle}>🚀 Share & Earn</h3>
        <p style={styles.shareText}>
          Every salesperson who signs up through your link earns you recurring income.
        </p>
        <div style={styles.shareActions}>
          <button onClick={copyLink} style={styles.shareBtnPrimary}>
            {copied ? '✅ Link Copied!' : '📋 Copy Referral Link'}
          </button>
          <button
            onClick={() => {
              const text = `Check out Tracksy — the ultimate car sales tracker app! Track your deals, commissions, and activities all in one place. Try it free for 14 days: ${buildReferralLink(affiliate.referralCode)}`;
              if (navigator.share) {
                navigator.share({ title: 'Tracksy', text, url: buildReferralLink(affiliate.referralCode) });
              } else {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
              }
            }}
            style={styles.shareBtnSecondary}
          >
            📤 Share via...
          </button>
        </div>
      </div>

      <p style={styles.footer}>
        Tracksy Affiliate Portal · {new Date().getFullYear()}
      </p>
    </div>
  );
}

// ─── STYLES ─────────────────────────────────────────────────────────────────
const styles = {
  // Loading
  loadingWrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #0a0a0a 0%, #111 50%, #0a0f0a 100%)',
  },
  spinner: {
    width: 36, height: 36, border: '3px solid rgba(255,255,255,.1)',
    borderTopColor: '#00ff88', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: { color: 'rgba(255,255,255,.5)', marginTop: 16, fontSize: 14 },

  // Login
  loginWrap: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #0a0a0a 0%, #111 50%, #0a0f0a 100%)',
    padding: 20,
  },
  loginCard: {
    width: '100%', maxWidth: 400, background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(255,255,255,.08)', borderRadius: 20, padding: 32,
  },
  loginLogo: {
    textAlign: 'center', marginBottom: 32,
  },
  loginLogoText: {
    display: 'block', fontSize: 28, fontWeight: 900, letterSpacing: 4,
    background: 'linear-gradient(135deg, #00ff88, #00ccff)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  loginLogoSub: {
    display: 'block', fontSize: 13, color: 'rgba(255,255,255,.45)',
    fontWeight: 600, marginTop: 6, letterSpacing: 2, textTransform: 'uppercase',
  },
  loginForm: { display: 'flex', flexDirection: 'column', gap: 18 },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  inputLabel: { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: 1 },
  input: {
    width: '100%', padding: '14px 16px', background: 'rgba(255,255,255,.06)',
    border: '1px solid rgba(255,255,255,.1)', borderRadius: 12, color: '#fff',
    fontSize: 15, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .2s',
  },
  passwordWrap: { position: 'relative' },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 4,
  },
  errorMsg: {
    color: '#ff4d4d', fontSize: 13, fontWeight: 600, textAlign: 'center',
    padding: '10px 14px', background: 'rgba(255,77,77,.08)', borderRadius: 8,
  },
  loginBtn: {
    width: '100%', padding: '16px', border: 'none', borderRadius: 14,
    background: 'linear-gradient(135deg, #00ff88, #00ccff)', color: '#000',
    fontSize: 16, fontWeight: 800, cursor: 'pointer', marginTop: 8,
    transition: 'transform .15s, box-shadow .15s',
  },
  logoutBtn: {
    width: '100%', padding: '14px', border: '1px solid rgba(255,77,77,.25)',
    borderRadius: 12, background: 'rgba(255,77,77,.08)', color: '#ff4d4d',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },

  // Main Dashboard
  container: {
    minHeight: '100vh', background: 'linear-gradient(160deg, #0a0a0a 0%, #111 50%, #0a0f0a 100%)',
    color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '0 0 40px',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '24px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.06)',
  },
  headerTitle: {
    fontSize: 22, fontWeight: 900, letterSpacing: 3, margin: 0,
    background: 'linear-gradient(135deg, #00ff88, #00ccff)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  headerSub: { fontSize: 11, color: 'rgba(255,255,255,.4)', fontWeight: 600, letterSpacing: 1.5, marginTop: 2, textTransform: 'uppercase' },
  headerRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  affiliateName: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,.8)' },
  headerLogout: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,.35)',
    fontSize: 12, cursor: 'pointer', fontWeight: 600, padding: 0,
  },

  // Link Banner
  linkBanner: {
    margin: '16px 16px 0', padding: '18px', borderRadius: 16,
    background: 'linear-gradient(135deg, rgba(0,255,136,.08), rgba(0,204,255,.08))',
    border: '1px solid rgba(0,255,136,.2)',
  },
  linkBannerInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  linkLabel: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
  linkUrl: { fontSize: 13, color: '#00ff88', fontWeight: 600, wordBreak: 'break-all' },
  copyBtn: {
    flexShrink: 0, padding: '10px 16px', borderRadius: 10,
    background: 'rgba(0,255,136,.15)', border: '1px solid rgba(0,255,136,.3)',
    color: '#00ff88', fontWeight: 700, fontSize: 13, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'all .15s',
  },
  linkCode: { marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,.4)', fontWeight: 600 },
  codeHighlight: { color: '#00ccff', fontWeight: 800, letterSpacing: 2 },

  // Stats Grid
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12,
    padding: '16px 16px 0',
  },
  statCard: {
    padding: '18px 16px', borderRadius: 14, background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.08)', textAlign: 'center',
  },
  statLabel: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 },
  statValue: { fontSize: 28, fontWeight: 900, color: '#fff', lineHeight: 1.1 },
  statSub: { fontSize: 11, color: 'rgba(255,255,255,.3)', fontWeight: 600, marginTop: 4 },

  // Earnings
  earningsCard: {
    margin: '16px 16px 0', padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)',
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, margin: '0 0 16px', color: '#fff' },
  earningsGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
  earningsRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', background: 'rgba(255,255,255,.03)', borderRadius: 10,
    border: '1px solid rgba(255,255,255,.05)',
  },
  earningsLabel: { fontSize: 13, color: 'rgba(255,255,255,.6)', fontWeight: 600 },
  earningsValue: { fontSize: 16, fontWeight: 800, color: '#fff' },
  commissionNote: {
    display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16,
    padding: '12px 14px', background: 'rgba(0,204,255,.06)', borderRadius: 10,
    border: '1px solid rgba(0,204,255,.15)', fontSize: 13, color: 'rgba(255,255,255,.6)',
    lineHeight: 1.5,
  },
  commissionIcon: { flexShrink: 0, fontSize: 14 },

  // Table
  tableCard: {
    margin: '16px 16px 0', padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)',
  },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  tableFilters: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterBtn: {
    padding: '6px 14px', borderRadius: 8, background: 'rgba(255,255,255,.05)',
    border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)',
    fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
  },
  filterBtnActive: {
    background: 'rgba(0,255,136,.12)', borderColor: 'rgba(0,255,136,.3)',
    color: '#00ff88',
  },
  emptyState: { textAlign: 'center', padding: '40px 20px' },
  referralList: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 },
  referralCard: {
    padding: '16px', borderRadius: 12, background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.06)', transition: 'border-color .15s',
  },
  referralTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  referralEmail: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,.85)' },
  referralDate: { fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 600, marginTop: 2 },
  statusBadge: {
    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
    borderRadius: 8, border: '1px solid', fontSize: 12,
  },
  referralBottom: { display: 'flex', gap: 16 },
  referralStat: { display: 'flex', flexDirection: 'column', gap: 2 },
  referralStatLabel: { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: 0.5 },
  referralStatValue: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,.8)' },

  // Share Card
  shareCard: {
    margin: '16px 16px 0', padding: '24px 20px', borderRadius: 16,
    background: 'linear-gradient(135deg, rgba(0,255,136,.06), rgba(0,204,255,.06))',
    border: '1px solid rgba(0,255,136,.15)', textAlign: 'center',
  },
  shareText: { fontSize: 14, color: 'rgba(255,255,255,.6)', lineHeight: 1.6, marginBottom: 18 },
  shareActions: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
  shareBtnPrimary: {
    padding: '14px 24px', borderRadius: 12, border: 'none',
    background: 'linear-gradient(135deg, #00ff88, #00ccff)', color: '#000',
    fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'transform .15s',
  },
  shareBtnSecondary: {
    padding: '14px 24px', borderRadius: 12, border: '1px solid rgba(0,204,255,.3)',
    background: 'rgba(0,204,255,.1)', color: '#00ccff',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
  },
  footer: {
    textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,.2)',
    fontWeight: 600, marginTop: 32,
  },
};

// Inject keyframe animation
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}
